import React, { useState } from "react";
import { Modal, ModalHeader } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { updateAgencyLTA } from "common/services/contracts.service";
import LTAForm from "./LTAForm";
import { toast } from "react-toastify";

const UpdateLTA: React.FC<{
  toggle: () => void;
  isModalOpen: boolean;
  fetchContracts: () => void;
  selectedRecord: any;
}> = ({ toggle, isModalOpen, fetchContracts, selectedRecord }) => {
  const [loading, setLoading] = useState<boolean>(false);
  // validation

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,
    initialValues: {
      contractTitle: selectedRecord?.contractTitle,
      contractNo: selectedRecord?.contractNo,
      contractor: {
        value: selectedRecord?.contractor?.id,
        label: `${selectedRecord?.contractor?.contractorName} ( ${selectedRecord?.contractor?.acronym} )`,
      },
      subContractedTo: {
        value: selectedRecord?.subContractedTo?.id,
        label: `${selectedRecord?.subContractedTo?.contractorName} ( ${selectedRecord?.subContractedTo?.acronym} )`,
      },
      contractRating: {
        value: selectedRecord?.contractRating?.id,
        label: selectedRecord?.contractRating?.name,
      },
      contractType: {
        value: selectedRecord?.contractType?.id,
        label: selectedRecord?.contractType?.name,
      },
      contractValue: selectedRecord?.contractValue,
      startDate:
        (selectedRecord?.startDate && new Date(selectedRecord?.startDate)) ||
        null,
      endDate:
        (selectedRecord?.endDate && new Date(selectedRecord?.endDate)) || null,
      isLTA: selectedRecord?.isLTA,
      regions:
        (selectedRecord?.location &&
          selectedRecord?.location.map((el: any) => {
            return {
              value: el?.id,
              label: el?.name,
            };
          })) ||
        [],
      additionalInformation: selectedRecord?.additionalInformation,
      isLTAUsableByOtherAgencies:
        selectedRecord?.isLTAUsableByOtherAgencies === true ? "Yes" : "No",
      selectedFiles:
        (selectedRecord?.ltaDocuments &&
          selectedRecord?.ltaDocuments?.map((file: any) => {
            return {
              id: file.id,
              name: file.name,
              formattedSize: file.size + " KB",
            };
          })) ||
        [],
    },
    validationSchema: Yup.object({
      contractTitle: Yup.string().required("Please enter contract title"),
      contractNo: Yup.string().required("Please enter contract number"),
      contractor: Yup.object().shape({
        value: Yup.number().required("Please select contractor"),
      }),
      subContractedTo: Yup.object().shape({
        value: Yup.number().nullable(),
      }),
      contractRating: Yup.object().shape({
        value: Yup.number().nullable(),
      }),
      contractType: Yup.object().shape({
        value: Yup.number().required("Please select contract type"),
      }),
      contractValue: Yup.number().required("Please enter contract value"),
      startDate: Yup.string(),
      endDate: Yup.string(),
      regions: Yup.array().of(
        Yup.object().shape({
          label: Yup.string().required("Country is required"),
          value: Yup.string().required("Country is required"),
        })
      ),
      additionalInformation: Yup.string(),
      isLTAUsableByOtherAgencies: Yup.string(),
      selectedFiles: Yup.array(),
    }),
    onSubmit: async (values: any) => {
      const data = {
        contractTitle: values["contractTitle"],
        contractor: values["contractor"]["value"],
        contractNo: values["contractNo"],
        subContractedTo: values["subContractedTo"]["value"],
        contractRating: values["contractRating"]["value"],
        contractType: values["contractType"]["value"],
        contractValue: values["contractValue"],
        startDate:
          values["startDate"] &&
          values["startDate"]?.toLocaleDateString("en-CA"),
        endDate:
          values["endDate"] && values["endDate"]?.toLocaleDateString("en-CA"),
        isLTA: values["isLTA"],
        location: values["regions"].map((el: any) => el.value),
        additionalInformation: values["additionalInformation"],
        isLTAUsableByOtherAgencies:
          values["isLTAUsableByOtherAgencies"] === "Yes" ? true : false,
        ltaDocuments: validation.values.selectedFiles.filter((file: any) => {
          if (file?.id) {
            return file.id;
          }
          return false;
        }),
      };

      setLoading(true);
      try {
        const formData = new FormData();
        values.selectedFiles.forEach((file: any) => {
          if (!file?.id) {
            formData.append("files.ltaDocuments", file, file.name);
          }
        });
        formData.append("data", JSON.stringify(data));
        await updateAgencyLTA(selectedRecord?.id, formData);
        fetchContracts();
        validation.resetForm();
        toggle();
        toast.success("LTA updated successfully", {});
      } catch (e: any) {
        toast.error("LTA update failed", {});
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Modal
      id="showModal"
      size="lg"
      isOpen={isModalOpen}
      toggle={toggle}
      centered
    >
      <ModalHeader className="bg-light p-3" toggle={toggle}>
        Update LTA
      </ModalHeader>

      <LTAForm
        validation={validation}
        isUpdate
        loading={loading}
        toggle={toggle}
        setLoading={setLoading}
      />
    </Modal>
  );
};

export default UpdateLTA;
